import { useAuthStore } from '@bemor/web-kit';
import {
    AsOfBadge,
    DateTime,
    Money,
    ObjectRef,
    PageHeader,
    RowActions,
    StatusBadge,
    type ListColumnDef,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { StaffChargeRow } from '@/api/types';
import { cardLabel, ReferenceId, threeDsLabel } from '@/components/charge';
import { canRefund, RefundSheet } from '@/components/refund-sheet';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';

const PATH = '/staff/charges';
const PAGE_KEY = 'charges';

/** The route's prefetch: the person's views, then the rows of the view they land on (BM-46 §2.0 + §2.4). */
export const chargesPrefetch = adminListPrefetch(PAGE_KEY, PATH);

const chargeFilters = filterDefs(PATH, {
    state: 'State',
    three_ds: '3-D Secure',
    range: 'Created',
});

const chargeHref = (id: string) => `/money/charges/${encodeURIComponent(id)}`;

/**
 * BM-51 § 12: every charge, newest first (the list contract's default). The state filter reaches a held charge
 * (`authorized`) that has no review item, so sits in no queue.
 */
export function ChargesPage() {
    const can = useAuthStore((s) => s.can);
    // bm-111 § 11: the row whose Refund sheet is open
    const [refunding, setRefunding] = useState<StaffChargeRow | null>(null);
    const columns: ListColumnDef<StaffChargeRow>[] = [
        {
            key: 'created_at',
            aliases: ['created'],
            label: 'Created',
            kind: 'datetime',
            sortKey: 'created_at',
            csv: (r) => r.created_at,
            cell: (r) => <DateTime value={r.created_at} empty="—" />,
            noTruncate: true,
        },
        {
            key: 'id',
            label: 'Charge',
            cell: (r) => (
                <Link
                    to={chargeHref(r.id)}
                    className="font-mono text-xs hover:underline"
                    onClick={(e) => e.stopPropagation()}
                >
                    {r.id}
                </Link>
            ),
            csv: (r) => r.id,
            boardCard: true,
            noTruncate: true,
        },
        {
            key: 'merchant_id',
            aliases: ['merchant'],
            label: 'Merchant',
            sortKey: 'merchant_id',
            csv: (r) => r.merchant_id,
            cell: (r) => <ObjectRef id={r.merchant_id} />,
        },
        {
            // bm-147 § 4: the charge's order, linked through adminObjectHref
            key: 'order_id',
            aliases: ['order'],
            label: 'Order',
            csv: (r) => r.order_id ?? '',
            cell: (r) => (r.order_id ? <ObjectRef id={r.order_id} /> : '—'),
        },
        {
            key: 'amount_cents',
            aliases: ['amount'],
            label: 'Amount',
            sortKey: 'amount_cents',
            csv: (r) => r.amount_cents,
            boardCard: true,
            cell: (r) => <Money cents={r.amount_cents} currency={r.currency} />,
            noTruncate: true,
        },
        {
            // design 1.5.1 (bm-127-design-v1.5.1 § 1): what was refunded, a muted "—" when nothing was; a money
            // column, so the calculation bar's default Sum lands on it. Not a list sort in the contract.
            key: 'refunded_cents',
            aliases: ['refunded'],
            label: 'Refunded',
            csv: (r) => r.refunded_cents,
            cell: (r) =>
                r.refunded_cents > 0 ? (
                    <Money cents={r.refunded_cents} currency={r.currency} />
                ) : (
                    <span className="text-muted-foreground">—</span>
                ),
            noTruncate: true,
        },
        {
            key: 'state',
            label: 'State',
            sortKey: 'state',
            cell: (r) => <StatusBadge status={r.state} domain="charge" />,
            csv: (r) => r.state,
            groupable: { key: 'state', statusDomain: 'charge' },
        },
        {
            key: 'card',
            label: 'Card',
            csv: (r) => cardLabel(r.card),
            cell: (r) => cardLabel(r.card),
        },
        {
            // bm-146 § 7: the provider's transaction id, copyable
            key: 'processor_txn_id',
            aliases: ['reference_id'],
            label: 'Reference ID',
            csv: (r) => r.processor_txn_id ?? '',
            cell: (r) => <ReferenceId value={r.processor_txn_id} />,
        },
        {
            // bm-146 § 9 (D-9): one column, 3DS · ECI 05 · 2.2.0 or No 3DS
            key: 'three_ds',
            label: '3-D Secure',
            csv: (r) => threeDsLabel(r.three_ds),
            cell: (r) => threeDsLabel(r.three_ds),
        },
        {
            key: 'actions',
            label: 'Actions',
            locked: 'end',
            actions: true,
            cell: (r) => (
                <RowActions
                    name={r.id}
                    items={[
                        {
                            label: 'Refund',
                            hidden: !canRefund(r, can),
                            onSelect: () => setRefunding(r),
                        },
                    ]}
                />
            ),
        },
    ];
    const { state, query } = useAdminList<StaffChargeRow>(PAGE_KEY, PATH, {
        columns,
        filters: chargeFilters,
    });
    return (
        <div>
            <PageHeader
                title="Charges"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            Card payments across merchants. Filter by state to
                            find a held payment.
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <AdminListPage
                noun="charges"
                state={state}
                query={query}
                rowKey={(r) => r.id}
                controls={{ state: { domain: 'charge' } }}
            />
            {refunding ? (
                <RefundSheet
                    bound={refunding}
                    onClose={() => setRefunding(null)}
                />
            ) : null}
        </div>
    );
}
